import { UpdatePageLayout, FormSection } from 'framework/ui/layout';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UpdateForm, FormFieldsData } from 'framework/form';
import PeopleManagementSpecifySection from 'domain/people/employee/shared/input/PeopleManagementSpecifySection';
import {
  HrInfoSectionUpdateSection,
  IdentityDocUpdateSection,
  MainInfoUpdateSection,
  MilitaryInfoUpdateSection,
  ContactInfoUpdateSection,
  FamilyUpdateSection,
  AccountsUpdateSection,
  RequisitesUpdateSection,
} from './sections';
import { useSearchParameter } from 'framework/routing';
import {
  useEmployeeService,
  FINALIZE_KEY,
  FINALIZE_STATE,
  READ_EMPLOYEE_WORKING_STATUS,
  UPDATE_EMPLOYEE_CONTACT_INFORMATION,
  UPDATE_EMPLOYEE_MAIN_PERSONAL_INFO,
  UPDATE_EMPLOYEE_PEOPLE_MANAGEMENT_RULES,
  useEmployeeUrl,
} from 'domain/shared/employee';
import { useAuthorization } from 'framework/authorization';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { isPolishOffice } from 'domain/shared/employee';
import { scrollToHash } from 'framework/utils';
import Prepopulation from 'domain/people/employee/prepopulation/Prepopulation';
import PrepopulateButton from 'domain/people/employee/prepopulation/PrepopulateButton';

const UpdateEmployeeProfile = () => {
  const state = useSearchParameter(FINALIZE_KEY);
  const { isGranted } = useAuthorization();
  const prepopulatedInputsRef = useRef(null);
  const { update, getForUpdate, getValidationSchema, isWorkplaceLocationNotRemote } = useEmployeeService();
  const id = useEmployeeId();
  const navigate = useNavigate();
  const { getSingleEntityUrl } = useEmployeeUrl();
  const isVisibleOfficeLocation = (data: FormFieldsData) =>
    isWorkplaceLocationNotRemote(data?.extendedInformation?.hrInformation?.workplaceLocation);

  const [fieldsExceptions, setFieldsExceptions] = useState({});

  const isFinalizing = state === FINALIZE_STATE;

  const isNotPolishOffice = (formData: FormFieldsData) => {
    return !isPolishOffice(String(formData.officeId));
  };

  useEffect(() => {
    if (isFinalizing) {
      setFieldsExceptions({
        initializationState: 'FINALIZED',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const scrollToPrepopulatedInputs = () => {
    if (prepopulatedInputsRef.current) {
      scrollToHash(prepopulatedInputsRef.current);
    }
  };

  const isGrantedUpdateMainInfo = isGranted(UPDATE_EMPLOYEE_MAIN_PERSONAL_INFO);
  const isGrantedUpdateContactInfo = isGranted(UPDATE_EMPLOYEE_CONTACT_INFORMATION);
  const isGrantedReadStatus = isGranted(READ_EMPLOYEE_WORKING_STATUS);

  return (
    <UpdateForm
      submitFormFunc={update}
      getDataFunc={getForUpdate}
      entityId={id}
      initialData={{
        maritalStatus: 'NOT_MARRIED',
      }}
      afterSubmit={{
        message: 'You are awesome! The worker has been successfully saved.',
        execute: () => {
          navigate(getSingleEntityUrl(), { replace: true });
          window.location.reload();
        },
      }}
      getValidationSchemaFunc={getValidationSchema}
      fieldsExceptions={fieldsExceptions}
    >
      <Prepopulation>
        <UpdatePageLayout
          title="Edit: Employee"
          titleVariant="h4"
          titleActions={<PrepopulateButton scrollToPrepopulatedInputs={scrollToPrepopulatedInputs} />}
          shouldScrollToErrorAfterLoading={isFinalizing}
        >
          <MainInfoUpdateSection
            isGrantedReadStatus={isGrantedReadStatus}
            isGrantedUpdateMainInfo={isGrantedUpdateMainInfo}
          />
          <IdentityDocUpdateSection isGranted={isGranted} />
          <ContactInfoUpdateSection
            isGrantedUpdateContactInfo={isGrantedUpdateContactInfo}
            ref={prepopulatedInputsRef}
          />
          <FamilyUpdateSection isGranted={isGranted} />
          <HrInfoSectionUpdateSection isGranted={isGranted} isVisibleOfficeLocation={isVisibleOfficeLocation} />
          <FormSection title="People management rules" isVisible={isGranted(UPDATE_EMPLOYEE_PEOPLE_MANAGEMENT_RULES)}>
            <PeopleManagementSpecifySection />
          </FormSection>
          <MilitaryInfoUpdateSection isGranted={isGranted} isNotPolishOffice={isNotPolishOffice} />
          <AccountsUpdateSection isGranted={isGranted} />
          <RequisitesUpdateSection isGranted={isGranted} />
        </UpdatePageLayout>
      </Prepopulation>
    </UpdateForm>
  );
};

export default UpdateEmployeeProfile;
